import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { DescriptionModal } from '~/pages/Catalog/DescriptionBlock';
import { CatalogCategory } from '~/stores/CategoriesStore';

import styles from './CategorySEOBlock.module.scss';

const CategorySEOBlock = ({ category }: { category: CatalogCategory }) => {
  const { t } = useTranslation();

  const { pathname, search } = useLocation();

  const title = category.data?.name ?? '';
  const text = category.data?.description ?? '';
  const totalItems = category.paginationRequest.total ?? 0;

  const textRef = useRef<Nullable<HTMLDivElement>>(null);

  const [openModal, setOpenModal] = useState(false);
  const [isCropped, setIsCropped] = useState(false);

  useEffect(() => {
    const handler = () => {
      const element = textRef.current;

      if (element) {
        const diff = element.scrollHeight - element.clientHeight;
        setIsCropped(diff > 14);
      }
    };

    handler();

    window.addEventListener('resize', handler);

    return () => window.removeEventListener('resize', handler);
  }, [text, title]);

  const schemaOrgData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: totalItems,
    },
    url: `${pathname}?${search.toString()}`,
    description: text,
  };

  return (
    <div
      className={styles.root}
      itemScope
      itemType="https://schema.org/CollectionPage"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrgData) }}
      />
      <div className={styles.seoHeader}>
        <h3 className={styles.seoTitle} itemProp="name">
          {title}
        </h3>
        <span className={styles.seoResults}>
          {category.paginationRequest.total} {t('results')}
        </span>
      </div>

      <div
        className={styles.seoDescription}
        ref={textRef}
        itemProp="description"
      >
        {text}
      </div>
      {isCropped && (
        <div>
          <span
            className={styles.showMoreBtn}
            role="button"
            tabIndex={-1}
            onClick={() => setOpenModal(true)}
          >
            {t('showMore')}
          </span>
        </div>
      )}

      <DescriptionModal
        open={openModal}
        title={title}
        text={text}
        onClose={() => setOpenModal(false)}
      />
    </div>
  );
};

export default observer(CategorySEOBlock);
